import { Cloudinary } from "@cloudinary/url-gen";
import { PDFDocument } from "pdf-lib";

export type UploadingFileStatus = {
  progress: number;
  status: "waiting" | "errored" | "loading" | "done";
};

export type CloudinaryUploadResponse = {
  resource_type: string;
  type: string;
  public_id: string;
  format: string;
  version: string;
  secure_url: string;
};

const cld = new Cloudinary({
  cloud: {
    cloudName: "dcmekntwa",
  },
});

/**
 * Upload un fichier unique vers Cloudinary et retourne l'URL
 * Version simplifiée pour les cas où on a besoin d'une Promise avec l'URL
 */
export const uploadSingleFile = (
  file: File,
  onProgress?: (progress: number) => void,
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const timestamp = Date.now().toString();
    const preset = "optimize";

    fetch(
      `/api/sign_cloudinary_upload?timestamp=${timestamp}&return_delete_token=true&upload_preset=${preset}`,
    )
      .then(async (signatureData) => {
        const { signature } = await signatureData.json();
        const url = `https://api.cloudinary.com/v1_1/${cld.getConfig().cloud?.cloudName}/upload`;

        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener("progress", (e) => {
          if (onProgress) {
            onProgress((e.loaded * 100.0) / e.total);
          }
        });

        xhr.upload.addEventListener("error", () => {
          reject(new Error("Upload failed"));
        });

        xhr.onreadystatechange = function () {
          if (xhr.readyState === 4) {
            if (xhr.status === 200) {
              const response: CloudinaryUploadResponse = JSON.parse(xhr.responseText);
              resolve(response.secure_url);
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}`));
            }
          }
        };

        const fd = new FormData();
        fd.append("upload_preset", preset);
        fd.append("file", file);
        fd.append("api_key", "642294451524929");
        fd.append("timestamp", timestamp);
        fd.append("return_delete_token", "true");
        fd.append("signature", signature);

        xhr.open("POST", url, true);
        xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
        xhr.send(fd);
      })
      .catch(reject);
  });
};

// Compteur au niveau module, et non par handler : le contrôleur Stimulus
// reconstruit son handler à chaque envoi (`buildUploader`), un
// compteur interne repartirait de 0 et deux lots successifs se marcheraient
// dessus dans le suivi de progression.
let uploadSeq = 0;

export const buildUploadHandler = (dependencies: {
  // `order` = rang du fichier dans la sélection de l'utilisateur. Les uploads
  // se terminent dans le désordre (une petite photo double une grosse) : sans
  // ce rang, un carrousel sort dans l'ordre d'arrivée réseau.
  addFile: (file: any, order: number) => void;
  updateFileStatus: (key: string, status: UploadingFileStatus) => void;
}) => {
  const { addFile, updateFileStatus } = dependencies;

  // NOTE: la signature Cloudinary ne peut PAS servir de clé d'identité.
  // Elle est calculée à partir des seuls params de la requête (timestamp +
  // return_delete_token, cf Api::CloudinaryController#sign_upload), et la
  // boucle d'envoi est synchrone : tous les fichiers d'un même lot tombent
  // sur le même timestamp, donc sur la même signature. Les indexer par
  // signature les faisait s'écraser dans le suivi de progression, et le lot
  // était considéré terminé dès la première photo arrivée.
  const generateUniqueUploadId = () => {
    return `uqid-${Date.now()}`;
  };

  const createRequest = (key: string) => {
    // on utilise XHR pour avoir accès au progrès de l'upload
    const xhr = new XMLHttpRequest();
    Object.entries({
      progress: (e: any) =>
        updateFileStatus(key, {
          status: "loading",
          progress: (e.loaded * 100.0) / e.total,
        }),
      loadstart: (_e: any) => updateFileStatus(key, { status: "loading", progress: 0 }),
      error: (_e: any) => {
        updateFileStatus(key, { status: "errored", progress: 0 });
      },
    }).forEach(([k, f]: [string, (e: any) => void]) => xhr.upload.addEventListener(k, f));
    return xhr;
  };

  const generateFormData = (file: Blob, signature: string, timestamp: string, preset?: string) => {
    const fd = new FormData();
    if (preset) fd.append("upload_preset", preset);
    fd.append("file", file);
    fd.append("api_key", "642294451524929");
    fd.append("timestamp", timestamp);
    fd.append("return_delete_token", "true");
    fd.append("signature", signature);
    return fd;
  };

  const handleFinalResponse = (
    response: { resource_type: string, type: string, public_id: string, format: string, version: string },
    key: string,
    order: number,
    fileSize: number
  ) => {
    // addFile AVANT le passage à "done" : c'est ce dernier qui déclenche le
    // flush du lot côté hook, le fichier doit déjà y être.
    const identifier = `${response.resource_type}/${response.type}/v${response.version}/${response.public_id}.${response.format}`;
    addFile({ ...response, identifier, byte_size: fileSize }, order);
    updateFileStatus(key, { status: "done", progress: 100 });
  }

  const smallUpload = (
    url: string,
    file: Blob,
    key: string,
    order: number,
    signature: string,
    timestamp: string,
    preset?: string,
  ) => {
    const xhr = createRequest(key);
    const fd = generateFormData(file, signature, timestamp, preset);
    xhr.onreadystatechange = function () {
      if (xhr.readyState != 4) return;

      // Le listener "error" de xhr.upload ne couvre que les coupures réseau :
      // sans ce else, un refus Cloudinary (413, 400, signature expirée…)
      // laissait le fichier en "loading" à vie et le lot n'était jamais flushé.
      if (xhr.status == 200) {
        var response = JSON.parse(xhr.responseText);
        handleFinalResponse(response, key, order, file.size)
      } else {
        console.error(`Cloudinary upload failed (${xhr.status})`, xhr.responseText);
        updateFileStatus(key, { status: "errored", progress: 0 });
      }
    };
    xhr.open("POST", url, true);
    xhr.setRequestHeader("X-Requested-With", "XMLHttpRequest");
    xhr.send(fd);
  };

  const bigUpload = (
    url: string,
    file: Blob,
    key: string,
    order: number,
    signature: string,
    timestamp: string,
    preset?: string,
  ) => {
    const uniqueUploadId = generateUniqueUploadId();
    const chunkSize = 5 * 1024 * 1024;
    const totalChunks = Math.ceil(file.size / chunkSize);
    let currentChunk = 0;

    const uploadChunk = async (start: number, end: number) => {
      const body = generateFormData(file.slice(start, end), signature, timestamp, preset);
      const contentRange = `bytes ${start}-${end - 1}/${file.size}`;
      try {
        const response = await fetch(url, {
          method: "POST",
          body,
          headers: {
            "X-Unique-Upload-Id": uniqueUploadId,
            "Content-Range": contentRange,
          },
        });

        if (!response.ok) {
          throw new Error("Chunk upload failed.");
        }

        currentChunk++;

        if (currentChunk < totalChunks) {
          const nextStart = currentChunk * chunkSize;
          const nextEnd = Math.min(nextStart + chunkSize, file.size);
          uploadChunk(nextStart, nextEnd);
          updateFileStatus(key, {
            status: "loading",
            progress: Math.round((nextStart / file.size) * 100),
          });
        } else {
          const fetchResponse = await response.json();
          handleFinalResponse(fetchResponse, key, order, file.size)
        }
      } catch (error) {
        console.error("Error uploading chunk:", error);
        // Sans ça le fichier restait "loading" et bloquait le flush du lot.
        updateFileStatus(key, { status: "errored", progress: 0 });
      }
    };
    const start = 0;
    const end = Math.min(chunkSize, file.size);
    uploadChunk(start, end);
  };

  // Preset "optimize" (1920 px max, q_auto:good) explicite pour les images : sans lui, on ne
  // dépend que du preset par défaut du compte Cloudinary, qui a cessé de s'appliquer de juin à
  // septembre 2026 (images stockées jusqu'à 40 Mo). Vidéos et PDF n'y passent pas : c'est une
  // transformation pensée pour des images.
  const presetFor = (file: File) => (file.type?.startsWith("image/") ? "optimize" : undefined);

  const uploadFile = (file: File, key: string, order: number) => {
    const timestamp = Date.now().toString();
    const preset = presetFor(file);
    // Le preset fait partie des paramètres signés : il doit figurer dans la demande de signature.
    const presetParam = preset ? `&upload_preset=${preset}` : "";

    fetch(`/api/sign_cloudinary_upload?timestamp=${timestamp}&return_delete_token=true${presetParam}`)
      .then(async (signatureData) => {
        const { signature } = await signatureData.json();
        const url = `https://api.cloudinary.com/v1_1/${cld.getConfig().cloud?.cloudName}/upload`;

        if (file.size > 50_000_000) {
          bigUpload(url, file, key, order, signature, timestamp, preset);
        } else {
          smallUpload(url, file, key, order, signature, timestamp, preset);
        }
      })
      .catch((error) => {
        console.error("Cloudinary signature request failed:", error);
        updateFileStatus(key, { status: "errored", progress: 0 });
      });
  };

  return async (files: File[]) => {
    // On enregistre TOUT le lot en "waiting" avant de lancer le moindre envoi.
    // Sinon un fichier peut finir avant que le suivant ait émis son loadstart,
    // et le lot est considéré complet alors qu'il en reste en vol.
    const queued = files.map((file) => {
      const order = (uploadSeq += 1);
      return { file, order, key: `upload-${order}` };
    });
    queued.forEach(({ key }) => updateFileStatus(key, { status: "waiting", progress: 0 }));
    queued.forEach(({ file, key, order }) => uploadFile(file, key, order));
  };
};
